import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Language, Me } from "../../../shared/api.ts";
import { api, queryKeys } from "@/lib/api.ts";
import { fetchMe } from "@/lib/queries.ts";
import { readStorage, writeStorage } from "@/lib/utils.ts";
import { en } from "./en.ts";
import { createFormatters, type Formatters } from "./format.ts";
import { uk, type Dict } from "./uk.ts";

export type { Dict } from "./uk.ts";

const dictionaries: Record<Language, Dict> = { uk, en };

/** The dictionary of a language, for text shown right after switching to it. */
export function dictionaryFor(language: Language): Dict {
  return dictionaries[language];
}
const STORAGE_KEY = "box33.language";

type I18nValue = {
  language: Language;
  t: Dict;
  fmt: Formatters;
  /** Switches the language; signed in, it is also saved with `PATCH /api/me`. */
  setLanguage: (language: Language) => void;
};

const I18nContext = createContext<I18nValue | null>(null);

function storedLanguage(): Language {
  return readStorage(STORAGE_KEY) === "en" ? "en" : "uk";
}

/**
 * Holds the interface language. Before sign-in it comes from localStorage;
 * after sign-in from `Me.user.language`.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [language, setLanguageState] = useState<Language>(storedLanguage);
  const me = useQuery({ queryKey: queryKeys.me, queryFn: fetchMe, staleTime: 60_000 });
  const meLanguage = me.data?.user.language ?? null;

  // The signed-in user's language wins over the one stored in this browser.
  useEffect(() => {
    if (meLanguage) {
      setLanguageState(meLanguage);
      writeStorage(STORAGE_KEY, meLanguage);
    }
  }, [meLanguage]);

  useEffect(() => {
    const dict = dictionaries[language];
    document.documentElement.lang = dict.meta.htmlLang;
  }, [language]);

  const { mutate: saveLanguage } = useMutation({
    mutationFn: (next: Language) => api.updateMe({ language: next }),
    onSuccess: (updated) => {
      if (updated && typeof updated === "object" && "user" in updated) {
        queryClient.setQueryData(queryKeys.me, updated);
      }
    },
  });

  const setLanguage = useCallback(
    (next: Language) => {
      setLanguageState(next);
      writeStorage(STORAGE_KEY, next);
      const current = queryClient.getQueryData<Me | null>(queryKeys.me);
      if (current) {
        const updated: Me = { ...current, user: { ...current.user, language: next } };
        queryClient.setQueryData(queryKeys.me, updated);
        saveLanguage(next);
      }
    },
    [queryClient, saveLanguage],
  );

  const value = useMemo<I18nValue>(() => {
    const t = dictionaries[language];
    return { language, t, fmt: createFormatters(t), setLanguage };
  }, [language, setLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return value;
}
